import { defineStore } from 'pinia';
import useAuthStore from './auth';
import {
   handleApiRequest,
   defaultResponseStatus,
} from '~/functions/handleApiRequest';
import type { ResponseStatus } from '~/lms';
import type {
   PaginatedResults,
   BlogPost as BlogPost,
   BlogPostCategory,
} from '~/types/types';
import axios from 'axios';

interface BlogSearchParams {
   page?: number;
   slug?: string;
   category?: string;
   search?: string;
   club?: string;
   tournament?: string;
   level?: string;
   handicap?: string;
   country?: string;
   sort?: string;
   order?: 'asc' | 'desc';
   per_page?: number;
}

interface BlogStoreState {
   lmsApiBaseurl: string;
   isFetchingPosts: boolean;
   isFetchingSinglepost: boolean;
   postsResponseStatus: ResponseStatus;
   singlePostResponseStatus: ResponseStatus;
   currentPost: BlogPost | undefined;
   paginatedPosts: PaginatedResults<BlogPost> | undefined;
   currentSearchParams: BlogSearchParams;
   currentCategoryName: string | undefined;
   // Blog options state
   blogOptions: {
      clubs: string[];
      tournaments: string[];
      levels: string[];
      handicaps: string[];
      categories: string[];
      countryDetails: Array<{
         code: string;
         name: string;
         emoji: string;
      }>;
   };
   isFetchingBlogOptions: boolean;
   blogOptionsResponseStatus: ResponseStatus;
}

interface BlogStoreActions {
   /**
    * @deprecated Use `useBlogPosts()` composable instead (TanStack Query)
    * @see composables/useBlog.ts
    */
   setPosts(page?: number, slug?: string): void;
   /**
    * @deprecated Use `useBlogPosts()` composable instead (TanStack Query)
    * @see composables/useBlog.ts
    */
   fetchPosts(searchParams?: BlogSearchParams): void;
   /**
    * @deprecated Use `useBlogPost()` composable instead (TanStack Query)
    * @see composables/useBlog.ts
    */
   setCurrentPost(slug: string): void;
   /**
    * @deprecated No longer needed with TanStack Query (automatic cache management)
    */
   clearPosts(): void;
   /**
    * @deprecated No longer needed with TanStack Query (automatic cache management)
    */
   clearCurrentPost(): void;
   /**
    * @deprecated Category name is now extracted directly from posts data
    */
   setCategoryName(categoryName: string | undefined): void;
   /**
    * @deprecated Use `useBlogOptions()` composable instead (TanStack Query)
    * @see composables/useBlog.ts
    */
   setBlogOptions(): void;
}

const useBlogStore = defineStore<string, BlogStoreState, {}, BlogStoreActions>(
   'blog',
   {
      state: (): BlogStoreState => {
         return {
            lmsApiBaseurl: '',
            isFetchingPosts: false,
            isFetchingSinglepost: false,
            postsResponseStatus: defaultResponseStatus,
            singlePostResponseStatus: defaultResponseStatus,
            currentPost: undefined,
            paginatedPosts: undefined,
            currentSearchParams: {},
            currentCategoryName: undefined,
            // Blog options state
            blogOptions: {
               clubs: [],
               tournaments: [],
               levels: [],
               handicaps: [],
               categories: [],
               countryDetails: [],
            },
            isFetchingBlogOptions: false,
            blogOptionsResponseStatus: defaultResponseStatus,
         };
      },
      actions: {
         async setPosts(page?: number, slug?: string) {
            const searchParams: BlogSearchParams = { page, slug };
            await this.fetchPosts(searchParams);
         },

         async fetchPosts(searchParams: BlogSearchParams = {}) {
            if (!useAuthStore().authHeaders) {
               return;
            }

            if (this.isFetchingPosts) {
               return;
            }

            // Store current search params for future reference
            this.currentSearchParams = { ...searchParams };

            const params = new URLSearchParams();

            // Add all search parameters to the URL
            Object.entries(searchParams).forEach(([key, value]) => {
               if (value !== undefined && value !== null && value !== '') {
                  params.set(key, value.toString());
               }
            });

            const queryString = params.toString();
            const url = `${this.lmsApiBaseurl}blog-posts${queryString ? `?${queryString}` : ''}`;

            const result = await handleApiRequest(
               this,
               'isFetchingPosts',
               'postsResponseStatus',
               () =>
                  axios.get(url, {
                     headers: useAuthStore().authHeaders,
                  }),
               { ref: 'paginatedPosts', value: undefined },
            );

            if (result) {
               this.paginatedPosts = result;
            }
         },

         async setCurrentPost(slug: string) {
            if (!useAuthStore().authHeaders) {
               return;
            }

            if (this.isFetchingSinglepost) {
               return;
            }

            const result = await handleApiRequest(
               this,
               'isFetchingSinglepost',
               'singlePostResponseStatus',
               () =>
                  axios.get(`${this.lmsApiBaseurl}blog-posts/${slug}`, {
                     headers: useAuthStore().authHeaders,
                  }),
               { ref: 'currentPost', value: undefined },
            );

            if (result?.data) {
               this.currentPost = result.data;
            }
         },

         clearCurrentPost() {
            this.currentPost = undefined;
            this.singlePostResponseStatus = defaultResponseStatus;
         },

         clearPosts() {
            this.paginatedPosts = undefined;
            this.postsResponseStatus = defaultResponseStatus;
            this.currentSearchParams = {};
            this.currentCategoryName = undefined;
         },

         setCategoryName(categoryName: string | undefined) {
            this.currentCategoryName = categoryName;
         },

         async setBlogOptions() {
            if (!useAuthStore().authHeaders) {
               return;
            }

            if (this.isFetchingBlogOptions) {
               return;
            }

            // Single API call for all option types
            const result = await handleApiRequest(
               this,
               'isFetchingBlogOptions',
               'blogOptionsResponseStatus',
               () => axios.get(`${this.lmsApiBaseurl}blog-options`, {
                  headers: useAuthStore().authHeaders,
               }),
               { ref: 'blogOptions', value: { clubs: [], tournaments: [], levels: [], handicaps: [], categories: [], countryDetails: [] } },
            );

            if (result?.data) {
               this.blogOptions = {
                  clubs: result.data.club || [],
                  tournaments: result.data.tournament || [],
                  levels: result.data.level || [],
                  handicaps: result.data.handicap || [],
                  categories: result.data.categories || [],
                  countryDetails: result.data.country || [],
               };
            }
         },
      },
   },
);

export default useBlogStore;
